import React, { useEffect, useState } from "react";
import { Breadcrumb, Layout, Card, Typography, List, Tag, Switch, Select, InputNumber, Button, Space, Divider, notification, Tooltip } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetAllOrganisationsQuery, useGetOrganisationFeaturesQuery, useUpdateOrganisationFeaturesMutation } from "../../services/nestjs-base-api/admin-api";
import { InfoCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { FeatureTypeEnum } from "../../enums/FeatureTypeEnum";
import { FeatureStatusEnum } from "../../enums/FeatureStatusEnum";
import { PlanLevelEnum } from "../../enums/PlanLevelEnum";

const { Content } = Layout;
const { Title, Text } = Typography;

interface IFeatureSettings {
  organisation: {
    id: number;
    name: string;
    plan: PlanLevelEnum;
  };
  features: Array<{
    name: FeatureTypeEnum;
    status: FeatureStatusEnum;
    value: any;
    unlockLevel: PlanLevelEnum;
  }>;
}

const AdminFeaturesPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<number | null>(null);
  const [featuresData, setFeaturesData] = useState<IFeatureSettings | null>(null);
  
  // Fetch all organizations
  const {
    data: organisations,
    error: organisationsError,
    isLoading: isLoadingOrganisations
  } = useGetAllOrganisationsQuery({
    jwtToken: user.jwtToken
  });

  // Fetch features for selected organization
  const {
    data,
    error,
    isLoading
  } = useGetOrganisationFeaturesQuery({
    jwtToken: user.jwtToken,
    organisationId: selectedOrganisationId || 0
  }, { skip: !selectedOrganisationId });

  const [updateFeatures] = useUpdateOrganisationFeaturesMutation();

  useEffect(() => {
    if (error || organisationsError) {
      if (
        (error && 'status' in error && error.status === 401) ||
        (organisationsError && 'status' in organisationsError && organisationsError.status === 401)
      ) {
        logout();
      }
    }
  }, [error, organisationsError, logout]);

  // Update features data when API responds
  useEffect(() => {
    if (data) {
      // Ensure we have all required properties before setting the data
      if (data.organisation && data.features) {
        // Ensure organisation has a plan property, defaulting to Plan1 if missing
        const processedData = {
          ...data,
          organisation: {
            ...data.organisation,
            // Convert string plan to enum if needed
            plan: data.organisation.plan ?
              (typeof data.organisation.plan === 'string' ?
                // Handle string plans like "PLAN_1"
                data.organisation.plan === "PLAN_1" ? PlanLevelEnum.Plan1 :
                data.organisation.plan === "PLAN_2" ? PlanLevelEnum.Plan2 :
                data.organisation.plan === "PLAN_3" ? PlanLevelEnum.Plan3 :
                data.organisation.plan === "PLAN_4" ? PlanLevelEnum.Plan4 :
                PlanLevelEnum.Plan1
              : data.organisation.plan)
              : PlanLevelEnum.Plan1
          }
        };

        setFeaturesData(processedData);
      } else {
        console.error("Invalid data format received from API", data);
      }
    } else if (!selectedOrganisationId) {
      // Demo data for UI when no organisation is selected
      setFeaturesData({
        organisation: {
          id: 0,
          name: "Select an organisation",
          plan: PlanLevelEnum.Plan1
        },
        features: [
          {
            name: FeatureTypeEnum.MaxEvents,
            status: FeatureStatusEnum.Enabled,
            value: 5,
            unlockLevel: PlanLevelEnum.Plan1
          },
          {
            name: FeatureTypeEnum.MaxGalleriesPerEvent,
            status: FeatureStatusEnum.Enabled,
            value: 1,
            unlockLevel: PlanLevelEnum.Plan1
          },
          {
            name: FeatureTypeEnum.MaxPhotoSize,
            status: FeatureStatusEnum.Enabled,
            value: 5,
            unlockLevel: PlanLevelEnum.Plan1
          },
          {
            name: FeatureTypeEnum.MaxPhotosPerGallery,
            status: FeatureStatusEnum.Enabled,
            value: 1000,
            unlockLevel: PlanLevelEnum.Plan1
          },
          {
            name: FeatureTypeEnum.UseFacialRecognition,
            status: FeatureStatusEnum.Enabled,
            value: true,
            unlockLevel: PlanLevelEnum.Plan1
          },
          {
            name: FeatureTypeEnum.AllowBulkUpload,
            status: FeatureStatusEnum.Disabled,
            value: false,
            unlockLevel: PlanLevelEnum.Plan2
          },
          {
            name: FeatureTypeEnum.AllowPhotoExport,
            status: FeatureStatusEnum.Disabled,
            value: false,
            unlockLevel: PlanLevelEnum.Plan2
          },
          {
            name: FeatureTypeEnum.AllowApiAccess,
            status: FeatureStatusEnum.Disabled,
            value: false,
            unlockLevel: PlanLevelEnum.Plan4
          }
        ]
      });
    }
  }, [data, selectedOrganisationId]);

  // Function to get nice display name for feature
  const getFeatureName = (feature: FeatureTypeEnum): string => {
    switch (feature) {
      case FeatureTypeEnum.MaxEvents:
        return intl.formatMessage({ id: 'features.maxEvents.name' }) || 'Maximum Events';
      case FeatureTypeEnum.MaxGalleriesPerEvent:
        return intl.formatMessage({ id: 'features.maxGalleriesPerEvent.name' }) || 'Max Galleries Per Event';
      case FeatureTypeEnum.MaxPhotoSize:
        return intl.formatMessage({ id: 'features.maxPhotoSize' }) || 'Maximum Photo Size (MB)';
      case FeatureTypeEnum.MaxPhotosPerGallery:
        return intl.formatMessage({ id: 'features.maxPhotosPerGallery.name' }) || 'Max Photos Per Gallery';
      case FeatureTypeEnum.UseFacialRecognition:
        return intl.formatMessage({ id: 'features.useFacialRecognition' }) || 'Use Facial Recognition';
      case FeatureTypeEnum.AllowBulkUpload:
        return intl.formatMessage({ id: 'features.allowBulkUpload' }) || 'Allow Bulk Upload';
      case FeatureTypeEnum.AllowPhotoExport:
        return intl.formatMessage({ id: 'features.allowPhotoExport' }) || 'Allow Photo Export';
      case FeatureTypeEnum.AllowApiAccess:
        return intl.formatMessage({ id: 'features.allowApiAccess' }) || 'Allow API Access';
      default:
        return String(feature);
    }
  };

  // Get plan name based on enum
  const getPlanName = (plan: PlanLevelEnum): string => {
    switch (plan) {
      case PlanLevelEnum.Plan1:
        return intl.formatMessage({ id: 'organisation.plan.basic' }) || 'Basic';
      case PlanLevelEnum.Plan2:
        return intl.formatMessage({ id: 'organisation.plan.standard' }) || 'Standard';
      case PlanLevelEnum.Plan3:
        return intl.formatMessage({ id: 'organisation.plan.professional' }) || 'Professional';
      case PlanLevelEnum.Plan4:
        return intl.formatMessage({ id: 'organisation.plan.enterprise' }) || 'Enterprise';
      default:
        return String(plan) || 'Basic';
    }
  };

  // Function to get plan color based on enum
  const getPlanColor = (plan: PlanLevelEnum | string): string => {
    // Convert string plans to enum if needed
    const planEnum = typeof plan === 'string' ?
      plan === "PLAN_1" ? PlanLevelEnum.Plan1 :
      plan === "PLAN_2" ? PlanLevelEnum.Plan2 :
      plan === "PLAN_3" ? PlanLevelEnum.Plan3 :
      plan === "PLAN_4" ? PlanLevelEnum.Plan4 :
      PlanLevelEnum.Plan1
    : plan;

    switch (planEnum) {
      case PlanLevelEnum.Plan1:
        return 'blue';
      case PlanLevelEnum.Plan2:
        return 'cyan';
      case PlanLevelEnum.Plan3:
        return 'purple';
      case PlanLevelEnum.Plan4:
        return 'gold';
      default:
        return 'default';
    }
  };

  // Helper function to safely compare plan levels
  const isPlanHigherThan = (planA: PlanLevelEnum | string, planB: PlanLevelEnum | string): boolean => {
    // Convert to enum values if needed
    const planAEnum = typeof planA === 'string' ?
      planA === "PLAN_1" ? PlanLevelEnum.Plan1 :
      planA === "PLAN_2" ? PlanLevelEnum.Plan2 :
      planA === "PLAN_3" ? PlanLevelEnum.Plan3 :
      planA === "PLAN_4" ? PlanLevelEnum.Plan4 :
      PlanLevelEnum.Plan1
    : planA;

    const planBEnum = typeof planB === 'string' ?
      planB === "PLAN_1" ? PlanLevelEnum.Plan1 :
      planB === "PLAN_2" ? PlanLevelEnum.Plan2 :
      planB === "PLAN_3" ? PlanLevelEnum.Plan3 :
      planB === "PLAN_4" ? PlanLevelEnum.Plan4 :
      PlanLevelEnum.Plan1
    : planB;

    return planAEnum > planBEnum;
  };

  // Function to handle feature value change
  const handleFeatureValueChange = (featureName: FeatureTypeEnum, value: any) => {
    if (!featuresData) return;

    setFeaturesData({
      ...featuresData,
      features: featuresData.features.map(feature => 
        feature.name === featureName ? { ...feature, value } : feature
      )
    });
  };

  // Function to handle feature status change
  const handleFeatureStatusChange = (featureName: FeatureTypeEnum, status: boolean) => {
    if (!featuresData) return;

    setFeaturesData({
      ...featuresData,
      features: featuresData.features.map(feature => 
        feature.name === featureName ? 
          { ...feature, status: status ? FeatureStatusEnum.Enabled : FeatureStatusEnum.Disabled } 
          : feature
      )
    });
  };

  // Function to save feature changes
  const handleSaveFeatures = async () => {
    if (!featuresData || !selectedOrganisationId) {
      notification.error({
        message: intl.formatMessage({ id: 'admin.features.saveError' }) || 'Save Failed',
        description: intl.formatMessage({ id: 'admin.features.selectOrganisation' }) || 'Please select an organisation first.',
      });
      return;
    }

    try {
      await updateFeatures({
        jwtToken: user.jwtToken,
        organisationId: selectedOrganisationId,
        payload: {
          features: featuresData.features
        }
      }).unwrap();
      
      notification.success({
        message: intl.formatMessage({ id: 'admin.features.saveSuccess' }) || 'Features Saved',
        description: intl.formatMessage(
          { id: 'admin.features.saveSuccessDesc' },
          { name: featuresData.organisation.name }
        ) || `Features for ${featuresData.organisation.name} have been successfully saved.`,
      });
    } catch (err) {
      notification.error({
        message: intl.formatMessage({ id: 'admin.features.saveError' }) || 'Save Failed',
        description: intl.formatMessage({ id: 'admin.features.saveErrorDesc' }) || 'Failed to save features. Please try again.',
      });
    }
  };

  // Function to render the appropriate input for a feature based on its type
  const renderFeatureInput = (feature: any) => {
    // For boolean features (switches)
    if (typeof feature.value === 'boolean') {
      return (
        <Switch 
          checked={feature.value} 
          onChange={(value) => handleFeatureValueChange(feature.name, value)}
          disabled={feature.status === FeatureStatusEnum.Disabled}
        />
      );
    }
    
    // For numeric features
    if (typeof feature.value === 'number') {
      let min = 0;
      let max = 1000;
      
      // Adjust min/max based on feature type
      switch (feature.name) {
        case FeatureTypeEnum.MaxEvents:
          max = 5;
          break;
        case FeatureTypeEnum.MaxGalleriesPerEvent:
          max = 1;
          break;
        case FeatureTypeEnum.MaxPhotoSize:
          max = 5;
          break;
        case FeatureTypeEnum.MaxPhotosPerGallery:
          max = 1000;
          break;
      }
      
      return (
        <InputNumber 
          min={min} 
          max={max} 
          value={feature.value}
          onChange={(value) => handleFeatureValueChange(feature.name, value)}
          disabled={feature.status === FeatureStatusEnum.Disabled}
        />
      );
    }
    
    // Default case
    return <Text>{String(feature.value)}</Text>;
  };

  return (
    <Content style={{ margin: '0 16px' }}>
      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.featuresName'} defaultMessage="Features" />
        </Breadcrumb.Item>
      </Breadcrumb>
      
      <Card
        title={
          <Title level={2}>
            <FormattedMessage id={'admin.features.title'} defaultMessage="Manage Organisation Features" />
          </Title>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ marginBottom: 24 }}>
            <Text>
              <FormattedMessage 
                id={'admin.features.selectOrganisation'} 
                defaultMessage="Select an organisation to manage its features:" 
              />
            </Text>
            <Select
              placeholder={intl.formatMessage({ id: 'admin.features.organisationPlaceholder' }) || "Select organisation"}
              style={{ width: 300, marginLeft: 16 }}
              onChange={setSelectedOrganisationId}
              value={selectedOrganisationId}
              loading={isLoadingOrganisations}
            >
              {organisations && organisations.map((org) => (
                <Select.Option key={org.id} value={org.id}>
                  {org.name}
                </Select.Option>
              ))}
            </Select>
          </div>
          
          {selectedOrganisationId ? (
            featuresData && featuresData.organisation && featuresData.organisation.plan && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Space align="center">
                    <Text strong>
                      <FormattedMessage id={'admin.features.currentPlan'} defaultMessage="Current Plan:" />
                    </Text>
                    <Tag color={getPlanColor(featuresData.organisation.plan)}>
                      {getPlanName(featuresData.organisation.plan)}
                    </Tag>
                    <Tooltip title={
                      intl.formatMessage(
                        { id: 'admin.features.planLimitsInfo' },
                        { plan: getPlanName(featuresData.organisation.plan) }
                      ) ||
                      `Some features are only available on higher plans than ${getPlanName(featuresData.organisation.plan)}`
                    }>
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                </div>

                <div style={{ position: 'relative', overflow: 'hidden' }}>
                  {/* DRAFT Watermark - only shown when organization is selected */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%) rotate(-30deg)',
                      pointerEvents: 'none',
                      zIndex: 1,
                      opacity: 0.15,
                    }}
                  >
                    <Typography.Text
                      style={{
                        fontSize: '100px',
                        fontWeight: 'bold',
                        color: '#ff4d4f',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      DRAFT
                    </Typography.Text>
                  </div>

                <List
                  header={
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                      <div style={{ flex: 1 }}>
                        <FormattedMessage id={'admin.features.featureName'} defaultMessage="Feature" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <FormattedMessage id={'admin.features.requiredPlan'} defaultMessage="Required Plan" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <FormattedMessage id={'admin.features.status'} defaultMessage="Status" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <FormattedMessage id={'admin.features.value'} defaultMessage="Value" />
                      </div>
                    </div>
                  }
                  bordered
                  dataSource={featuresData.features}
                  renderItem={feature => (
                    <List.Item>
                      <div style={{ flex: 1 }}>
                        <Text strong>{getFeatureName(feature.name)}</Text>
                      </div>
                      <div style={{ flex: 1 }}>
                        <Tag color={getPlanColor(feature.unlockLevel)}>
                          {getPlanName(feature.unlockLevel)}
                        </Tag>
                      </div>
                      <div style={{ flex: 1 }}>
                        <Switch
                          checked={feature.status === FeatureStatusEnum.Enabled}
                          onChange={(checked) => handleFeatureStatusChange(feature.name, checked)}
                          disabled={
                            featuresData &&
                            featuresData.organisation &&
                            featuresData.organisation.plan &&
                            feature.unlockLevel > featuresData.organisation.plan
                          }
                        />
                        {featuresData &&
                          featuresData.organisation &&
                          featuresData.organisation.plan &&
                          feature.unlockLevel > featuresData.organisation.plan && (
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            <FormattedMessage id={'admin.features.upgradePlanRequired'} defaultMessage="Upgrade plan required" />
                          </Text>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        {renderFeatureInput(feature)}
                      </div>
                    </List.Item>
                  )}
                />

                <Divider />

                <div style={{ textAlign: 'right' }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveFeatures}
                    disabled={true}
                  >
                    <Space>
                      <FormattedMessage id={'admin.features.saveFeatures'} defaultMessage="Save Features" />
                    </Space>
                  </Button>
                </div>
                </div> {/* Close the wrapper div for the watermark */}
              </>
            )
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Title level={4} type="secondary">
                <FormattedMessage
                  id={'admin.features.selectOrganisationFirst'}
                  defaultMessage="Please select an organisation first"
                />
              </Title>
            </div>
          )}
        </Space>
      </Card>
    </Content>
  );
};

export default AdminFeaturesPage;